import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  User, 
  Code2, 
  Award, 
  BookOpen, 
  Send,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  Maximize,
  Minimize
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import './Navbar.css';

const navItems = [
  { id: 'home', name: 'Home', icon: Home },
  { id: 'about', name: 'About', icon: User },
  { id: 'projects', name: 'Projects', icon: Code2 },
  { id: 'certifications', name: 'Certifications', icon: Award },
  { id: 'blogs', name: 'Blogs', icon: BookOpen },
  { id: 'contact', name: 'Contact', icon: Send }
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { theme, changeTheme } = useTheme();
  
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    // 1. Optimized scroll state for navbar appearance
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 2. Fallback check for scroll position because Suspense might delay elements
    const handleSectionScroll = () => {
      if (isScrollingRef.current) return;
      
      const scrollPosition = window.scrollY + window.innerHeight / 3;
      let currentId = null;

      for (const item of navItems) {
        const element = document.getElementById(item.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            currentId = item.id;
          }
        }
      }
      
      if (currentId) {
        setActiveSection(prev => (prev !== currentId ? currentId : prev));
      }
    };
    
    window.addEventListener('scroll', handleSectionScroll, { passive: true });

    // 3. Close theme menu when clicking outside
    const handleClickOutside = (event) => {
      if (showThemeMenu && !event.target.closest('.theme-switcher')) {
        setShowThemeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    // 4. Handle Fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // 5. Shortcut key for fullscreen (Shift + F)
    const handleKeyDown = (e) => {
      if (e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleSectionScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showThemeMenu]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };


  const scrollToSection = (id) => {
    setActiveSection(id);
    
    // Suspend scroll updates during the scroll animation
    isScrollingRef.current = true;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 1600); // Wait for lenis duration to finish

    const element = document.getElementById(id);
    if (element) {
      if (window.lenis) {
        window.lenis.scrollTo(element, {
          offset: -80,
          duration: 1.5,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
        });
      } else {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsOpen(false);
  };


  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <div 
          className="logo"
          onClick={() => scrollToSection('home')}
          style={{ cursor: 'pointer' }}
        >
          <motion.div 
            initial={{ opacity: 0, x: -20, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            whileHover={{ 
              scale: 1.1,
              filter: "brightness(1.1) drop-shadow(0 0 12px rgba(108, 92, 231, 0.4))",
            }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <img src="/image.png" alt="Rahul" className="navbar-logo-img" />
          </motion.div>
        </div>

        {/* Desktop Navigation */}
        <div className="nav-links">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <motion.div
                key={item.id}
                className={`nav-item ${isActive ? 'active' : ''}`}
                whileHover={{ y: -2 }}
                onClick={() => scrollToSection(item.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="nav-link-wrapper">
                  {isActive && (
                    <motion.div
                      className="active-indicator"
                      layoutId="activeNav"
                      initial={false}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  <motion.span 
                    className="nav-content"
                    style={{ position: 'relative', zIndex: 2 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <Icon className="nav-icon" size={18} />
                    {item.name}
                  </motion.span>
                </div>
              </motion.div>
            );
          })}
          
        </div>

        {/* Global Controls Group (Theme + Fullscreen) - Visible on Desktop AND Mobile */}
        <div className="nav-controls">
          {/* Theme Switcher */}
          <div className="theme-switcher">
            <motion.button
              className="theme-toggle"
              onClick={() => setShowThemeMenu(!showThemeMenu)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {theme === 'light' && <Sun size={20} />}
              {theme === 'dark' && <Moon size={20} />}
              {theme === 'system' && <Monitor size={20} />}
            </motion.button>
            
            <AnimatePresence>
              {showThemeMenu && (
                <motion.div
                  className="theme-menu"
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.button
                    className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => {
                      changeTheme('light');
                      setShowThemeMenu(false);
                    }}
                    whileHover={{ x: 4 }}
                  >
                    <Sun size={18} />
                    <span>Light</span>
                  </motion.button>
                  <motion.button
                    className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                    onClick={() => {
                      changeTheme('dark');
                      setShowThemeMenu(false);
                    }}
                    whileHover={{ x: 4 }}
                  >
                    <Moon size={18} />
                    <span>Dark</span>
                  </motion.button>
                  <motion.button
                    className={`theme-option ${theme === 'system' ? 'active' : ''}`}
                    onClick={() => {
                      changeTheme('system');
                      setShowThemeMenu(false);
                    }}
                    whileHover={{ x: 4 }}
                  >
                    <Monitor size={18} />
                    <span>System</span>
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <motion.button
            className="fullscreen-toggle"
            onClick={toggleFullscreen}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            title="Toggle Fullscreen (Shift + F)"
          >
            {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
          </motion.button>
        </div>

        {/* Mobile Toggle Button - Only visible when closed or for initial toggle */}
        {!isOpen && (
          <motion.button
            className="mobile-toggle"
            onClick={() => setIsOpen(true)}
            whileTap={{ scale: 0.9 }}
            whileHover={{ scale: 1.1 }}
          >
            <Menu size={24} />
          </motion.button>
        )}

        {/* Mobile Navigation Overlay */}
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Darkened backdrop */}
              <motion.div 
                className="mobile-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
              />
              
              <motion.div
                className="mobile-overlay"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
              >
                <div className="mobile-nav-header">
                  <span className="mobile-nav-title">Menu</span>
                  <motion.button
                    className="mobile-close-btn"
                    onClick={() => setIsOpen(false)}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={28} />
                  </motion.button>
                </div>

                {/* Profile Section Added to fill space */}
                <motion.div 
                  className="mobile-profile"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <img src="/image.png" alt="Profile" className="mobile-profile-img" />
                  <div className="mobile-profile-info">
                    <h3>Rahul Mishra</h3>
                    <p>Full Stack Developer</p>
                  </div>
                </motion.div>

                <div className="mobile-nav-content">
                  {navItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                      <motion.div
                        key={item.id}
                        className={`mobile-nav-item ${isActive ? 'active' : ''}`}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => scrollToSection(item.id)}
                      >
                        <div className="mobile-link-wrapper">
                          <Icon className="nav-icon" size={24} />
                          <span>{item.name}</span>
                        </div>
                        {isActive && <motion.div className="mobile-active-dot" layoutId="mobileActiveDot" />}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Social Links Added at the bottom */}
                <motion.div 
                  className="mobile-nav-footer-v2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="mobile-nav-socials">
                    <a href="https://github.com/machinelearningprodigy" target="_blank" rel="noreferrer"><Code2 size={20} /></a>
                    <a href="https://www.linkedin.com/in/rahul-mishra-9a8826269/" target="_blank" rel="noreferrer"><User size={20} /></a>
                    <a href="https://x.com/NitishM66803568" target="_blank" rel="noreferrer"><Send size={20} /></a>
                  </div>
                  <div className="mobile-made-with">
                    Made with <span className="heart-pump">❤️</span> by Rahul
                  </div>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
